/**
 * Tek merkez ayar dosyası.
 * Marka renkleri, netlik eşiği ve webhook/anahtarlar burada.
 *
 * ADIM 1 (2026-09-04): yüz/cilt analiz motoruna özgü ayarlar (referans oranlar,
 * çekim adımları, MediaPipe/poz eşikleri) bu iskeletten kaldırıldı — ağız içi
 * fotoğraf yükleme aracı bunlara ihtiyaç duymuyor. Kalan alanlar sonraki
 * adımlarda genişletilecek.
 */

/**
 * Netlik (blur) eşiği — Laplacian varyansı. Bunun altındaki kareler "bulanık" sayılır.
 * Cihaz/çözünürlüğe göre kalibre edin; tipik telefon fotoğrafında ~100-300 arası net kabul edilir.
 */
export const SHARPNESS_MIN_VARIANCE = 80;

/** Marka renkleri — CSS değişkenlerine yansır. Nexwell paletine göre buradan değiştirin. */
export const BRAND = {
  primary: "#0e7c86",
  primaryDark: "#0a5b62",
  accent: "#f4b740",
  bg: "#0b1416",
  surface: "#13212400",
  text: "#eef6f7",
  muted: "#9bb3b7",
  danger: "#e5614f",
  success: "#3fbf8f",
} as const;

/** Vite dışı runtime'larda (test/tsx) import.meta.env tanımsız olabilir — güvenli oku. */
const ENV = (import.meta as { env?: Record<string, string | undefined> }).env ?? {};

/** Lead webhook (KVKK onayı olmadan ASLA kullanılmaz). Boşsa form devre dışı. */
export const LEAD_WEBHOOK_URL = (ENV.VITE_LEAD_WEBHOOK_URL ?? "").trim();

/**
 * Cloudflare Turnstile site key (public by design — only the secret is
 * private). Default = the "bp6-lead-intake" widget shared with
 * cellavia/dentvisit's ConsultationForm (thenexwell.com scope confirmed
 * 2026-07-22).
 */
export const TURNSTILE_SITE_KEY =
  (ENV.VITE_TURNSTILE_SITE_KEY ?? "").trim() || "0x4AAAAAAD4zAy13OTVA7bQ5";

export const DEFAULT_LANG: "tr" | "en" = "en"; // spec: EN varsayılan (trafik ABD/UK)

/**
 * ADIM 2 (2026-09-04): 3-slot ağız içi fotoğraf yükleme akışı.
 * Her slot bir çekim açısını temsil eder; i18n anahtarları ile eşleşir
 * (src/i18n.ts'te `slot_<id>` / `hint_<id>` anahtarları tanımlı olmalı).
 */
export type PhotoSlotId = "front_smile" | "upper_occlusal" | "lower_occlusal";

export interface PhotoStep {
  id: PhotoSlotId;
  titleKey: string; // i18n anahtarı, örn "slot_front_smile"
  hintKey: string; // i18n anahtarı, tek cümlelik yönerge
  exampleImage: string; // "/examples/front-smile.webp" — henüz yok, bkz public/examples/README.md
  capture: "environment" | "user";
}

export const PHOTO_STEPS: PhotoStep[] = [
  {
    id: "front_smile",
    titleKey: "slot_front_smile",
    hintKey: "hint_front_smile",
    exampleImage: "/examples/front-smile.webp",
    capture: "user",
  },
  {
    id: "upper_occlusal",
    titleKey: "slot_upper_occlusal",
    hintKey: "hint_upper_occlusal",
    exampleImage: "/examples/upper-occlusal.webp",
    // Arka kamerayla kendi ağzını çekmek fiziksel olarak zor/imkansız (ayna
    // veya başkası gerekiyor) — Onur'un gerçek cihaz testi bulgusu. Üç slot
    // da varsayılan olarak ön kamerayla açılır; kamera değiştir butonuyla
    // gerekirse arkaya geçilebilir.
    capture: "user",
  },
  {
    id: "lower_occlusal",
    titleKey: "slot_lower_occlusal",
    hintKey: "hint_lower_occlusal",
    exampleImage: "/examples/lower-occlusal.webp",
    // Arka kamerayla kendi ağzını çekmek fiziksel olarak zor/imkansız (ayna
    // veya başkası gerekiyor) — Onur'un gerçek cihaz testi bulgusu. Üç slot
    // da varsayılan olarak ön kamerayla açılır; kamera değiştir butonuyla
    // gerekirse arkaya geçilebilir.
    capture: "user",
  },
];

/** Yeniden kodlama sonrası uzun kenar hedefi (px). Oran korunur. */
export const TARGET_MAX_EDGE_PX = 1600;

/** Ortalama luma eşiği — bunun altı "karanlık/ışık yetersiz" sayılır.
 * 60'tı — Onur'un gerçek cihaz testinde (2026-09-05) alt çene oklüzal
 * çekimde üst çenenin kendi gölgesi bu eşiği hiç geçilemez kıldığı bulundu
 * (fiziksel bir kısıt, kod hatası değil). 40'a düşürüldü; hâlâ yeterli
 * değilse yönergedeki "ek ışık kullanın" ipucuyla birlikte değerlendirilmeli. */
export const LOW_LIGHT_MIN_LUMA = 40;

/** Canlı kamera modu — 2026-09-04. Model dosyası repoya girmez
 * (.gitignore: public/models/*.tflite); eğitim çıktısından kopyalanır. */
export const LIVE_MODEL_URL = "./models/intraoral_classifier.tflite";
/** MediaPipe wasm dosyaları self-host edilir (CDN'e runtime bağımlılık yok). */
export const LIVE_WASM_BASE = "./mediapipe-wasm";
/** Otomatik çekim için gereken minimum sınıf güveni. Deneysel ilk değer (spec 6)
 * 0.75'ti — Onur'un gerçek cihaz testinde (2026-09-05) doğru açı+net+aydınlık
 * bir karede model doğru sınıfı %54 güvenle tanıdı ve hiç tetiklenmedi (eğitim
 * verisi azlığından oklüzal sınıflarda düşük güven, bkz. accuracy_report.json
 * per_class recall 0.33-0.67). 0.45'e düşürüldü — K3-b (yanlış-pozitif) riski
 * arttı, Görev 7'nin 10 yanlış-açı denemesiyle AYRICA ölçülmesi gerekiyor. */
export const LIVE_MIN_CONFIDENCE = 0.45;
/** Kare sınıflandırma aralığı (ms) — spec 4.3: yaklaşık 500ms. */
export const LIVE_CLASSIFY_INTERVAL_MS = 500;
/** Otomatik çekimden önce üst üste kaç kare tüm kapılardan geçmeli. */
export const LIVE_REQUIRED_CONSECUTIVE = 3;
